import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Upload, Save, Loader2, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminStarterKit() {
  const [kit, setKit] = useState({ enabled: false, title: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/starter-kit").then(({ data }) => setKit(data)).catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await api.put("/admin/starter-kit", { enabled: kit.enabled, title: kit.title, description: kit.description });
      toast.success("Starter kit settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  }

  async function handleUploadPdf() {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "application/pdf";
    input.onchange = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/admin/starter-kit/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setKit((k) => ({ ...k, pdf_path: data.pdf_path, pdf_filename: data.pdf_filename, pdf_size: data.pdf_size }));
        toast.success("PDF uploaded!");
      } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    };
    input.click();
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="bg-white rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-base">Free Starter Kit</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{kit.enabled ? "Live" : "Off"}</span>
            <Switch checked={kit.enabled} onCheckedChange={(v) => setKit({ ...kit, enabled: v })} />
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1 block">Title</label>
          <Input value={kit.title || ""} onChange={(e) => setKit({ ...kit, title: e.target.value })}
            placeholder="e.g., Burger Café Mini-Kit (3 pages)" className="rounded-xl" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1 block">Short description</label>
          <Textarea value={kit.description || ""} onChange={(e) => setKit({ ...kit, description: e.target.value })}
            placeholder="What's included in the free sample" rows={2} className="rounded-xl resize-none" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1.5 block">PDF File</label>
          {kit.pdf_path ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-2 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-green-800 truncate">{kit.pdf_filename || "starter-kit.pdf"}</p>
                <p className="text-xs text-green-600">{kit.pdf_size ? `${(kit.pdf_size / 1024 / 1024).toFixed(1)} MB` : ""}</p>
              </div>
              <a href={`${API_URL}/api/admin/starter-kit/pdf`} target="_blank" rel="noopener noreferrer">
                <Button type="button" variant="outline" size="sm" className="rounded-full text-xs h-7">
                  <ExternalLink className="w-3 h-3 mr-1" /> Open
                </Button>
              </a>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2">
              <p className="text-xs text-amber-700 font-semibold">No PDF uploaded yet.</p>
            </div>
          )}
          <Button type="button" variant="outline" className="w-full rounded-xl border-dashed border-2 h-10"
            onClick={handleUploadPdf}>
            <Upload className="w-4 h-4 mr-2" /> {kit.pdf_path ? "Replace PDF" : "Upload PDF"}
          </Button>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="w-full bg-coral hover:bg-coral-hover text-white rounded-full font-bold h-11">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Starter Kit Settings
        </Button>
      </div>
    </div>
  );
}
